import { Alert, Button, Field } from "@/components/ui";
import { CoachSettings, Media, MediaGroup } from "@/interfaces";
import { useCoachSettings, useSocialCoachSession } from "@/utils";
import { AlertCircle, WandSparkles } from "lucide-react";
import * as React from "react";
import TextareaAutosize from "react-textarea-autosize";
import { CaptionCoachDialog } from "@/components/CaptionCoach/CaptionCoachDialog";
import { HashTagSet } from "@/components/Forms/Prompt/components/HashTagSet";
import { HashTagsServices } from "@/services";
import { useEffect } from "react";

interface Props {
  values: {
    promptId?: string | number;
    message: string;
    hashtags: string;
    post?: MediaGroup;
  };
  disabled: boolean;
  handleSetFieldValue?: (field: string, value: any) => void;
  /** Shown in place of the caption box; hashtags stay editable. */
  captionNote?: React.ReactNode;
}

export function CaptionSection({
  values: { message, hashtags, post, promptId },
  disabled,
  handleSetFieldValue,
  captionNote,
}: Props) {
  const { coachId } = useSocialCoachSession();
  const { hasSetting } = useCoachSettings();
  const hasCaptionCoach = React.useMemo(() => {
    return hasSetting(CoachSettings.CAPTION_COACH, true);
  }, [hasSetting]);
  const [showAIModal, setShowAIModal] = React.useState<boolean>(false);

  const [tags, setTags] = React.useState<string>(hashtags);
  const originalMedia: Media[] = post?.media || [];
  const firstTranscript = originalMedia?.[0]?.transcripts?.[0]?.content;
  const [hashtagsListError, setHashtagsListError] = React.useState<string[] | undefined>();

  useEffect(() => {
    if (coachId && !promptId) {
      HashTagsServices.getCoachHashtags(coachId).then(response => {
        setTags(response.map(t => t.content).join(" "));
      });
    }
  }, [coachId, promptId]);

  return (
    <>
      {captionNote ? (
        <Field>
          <Field.Label>Prompt Caption</Field.Label>
          {captionNote}
        </Field>
      ) : (
        <Field>
          <div className="flex flex-row justify-between items-center">
            <Field.Label>Prompt Caption</Field.Label>
            {hasCaptionCoach && (
              <Button variant="ghost" size="sm" onClick={() => setShowAIModal(true)} className={"text-slate-600"}>
                <WandSparkles />
                Generate
              </Button>
            )}
          </div>
          <TextareaAutosize
            data-elm-id="promptInputMessage"
            id="caption"
            className="text-sm p-3 border rounded border-slate-300 bg-white text-slate-800"
            name="caption"
            placeholder={"Add a caption"}
            rows={3}
            disabled={disabled}
            value={message}
            onChange={data => {
              handleSetFieldValue!("withMessage", data.currentTarget.value + hashtags);
              handleSetFieldValue!("message", data.currentTarget.value);
              handleSetFieldValue!("richMessage", data.currentTarget.value);
            }}
          />
        </Field>
      )}

      <Field>
        <Field.Label>Prompt Hashtags</Field.Label>
        <div className={"flex flex-col gap-2"}>
          {hashtagsListError?.map((error, i) => (
            <Alert variant="destructive" key={`hashtag-error-${i}`}>
              <AlertCircle />
              <Alert.Title>{error}</Alert.Title>
            </Alert>
          ))}
          <HashTagSet
            title={"Hashtags..."}
            coachId={coachId!}
            onError={setHashtagsListError}
            defaultHashtagList={tags}
            onSuccess={newTags => {
              const hashtagsString = newTags.reduce(
                (accumulator, hashtag) => accumulator + "#" + hashtag.displayValue + " ",
                ""
              );
              if (newTags.length <= 30) {
                handleSetFieldValue!("hashtags", hashtagsString);
                handleSetFieldValue!("withMessage", message + hashtagsString);
              }
            }}
          />
        </div>
      </Field>

      {showAIModal && (
        <CaptionCoachDialog
          open={true}
          onOpenChange={setShowAIModal}
          context={firstTranscript}
          contextType={"video transcript"}
          handleAcceptedContent={({ caption, hashtags: addedHashtags }) => {
            setShowAIModal(false);
            handleSetFieldValue!("message", caption);
            handleSetFieldValue!("richMessage", caption);

            if (addedHashtags && addedHashtags.length > 0) {
              const newTags: string[] = [];
              if (hashtags.length > 0) {
                hashtags.split(" ").forEach(tag => newTags.push(tag));
              }

              addedHashtags.forEach(tag => {
                if (newTags.indexOf(tag) === -1) {
                  newTags.push(tag);
                }
              });
              setTags(newTags.join(" "));
            }
          }}
        />
      )}
    </>
  );
}
